/**
 * Shared IoT Source Configurations
 *
 * Reusable IoT data source builders for sensor readings
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { IoTSourceConfig, ActivityContext } from "../types"
import { DateTime } from "timezonecomplete"
import { toTimestamp } from "@hydrologiq/common/helpers/date"

const LAST_30_DAYS = 86400 * 30

/**
 * Create IoT source for pressure sensor readings
 */
export function pressureIoTSource(): IoTSourceConfig {
  return {
    query: gqlTypes.IoTDataLatestDocument,

    variablesBuilder: (ctx: ActivityContext, asset?: gqlTypes.AssetBasic, start?: DateTime) => {
      // Get asset from order (assets are now assigned at order level)
      const assetId = asset?.id ?? ctx.order?.assets?.[0]?.id
      const endEvent = ctx.activity.events?.find((e) => e.kind === gqlTypes.ActivityEventKind.Completed)

      const stopTime = endEvent?.created_at
        ? Math.floor(new Date(endEvent.created_at).getTime() / 1000)
        : Math.floor(Date.now() / 1000)

      const startTime = start ? toTimestamp(start, true) : stopTime - LAST_30_DAYS // Last 30 days

      return {
        installations: "",
        assets: assetId,
        field: "pressure_manifold",
        start: startTime,
        stop: stopTime,
        location: "Europe/London",
      }
    },

    transformer: (iotData: any) => {
      return iotData?.getIoTDataLatest?.value?.toFixed(1) || null
    },

    cacheDuration: 60000,
    loadingPlaceholder: "Reading pressure sensor...",
    fallbackValue: null,
  }
}

/**
 * Create IoT source for temperature sensor readings
 */
export function temperatureIoTSource(): IoTSourceConfig {
  return {
    query: gqlTypes.IoTDataLatestDocument,

    variablesBuilder: (ctx: ActivityContext, asset?: gqlTypes.AssetBasic, start?: DateTime) => {
      // Get asset from order (assets are now assigned at order level)
      const assetId = asset?.id ?? ctx.order?.assets?.[0]?.id
      const endEvent = ctx.activity.events?.find((e) => e.kind === gqlTypes.ActivityEventKind.Completed)

      const stopTime = endEvent?.created_at
        ? Math.floor(new Date(endEvent.created_at).getTime() / 1000)
        : Math.floor(Date.now() / 1000)

      const startTime = start ? toTimestamp(start, true) : stopTime - LAST_30_DAYS // Last 30 days

      return {
        installations: "",
        assets: assetId,
        field: "temperature",
        start: startTime,
        stop: stopTime,
        location: "Europe/London",
      }
    },

    transformer: (iotData: any) => {
      return iotData?.getIoTDataLatest?.value?.toFixed(1) || null
    },

    cacheDuration: 60000,
    loadingPlaceholder: "Reading temperature sensor...",
    fallbackValue: null,
  }
}

/**
 * Create IoT source for weight/quantity sensor readings
 */
export function quantityIoTSource(): IoTSourceConfig {
  return {
    query: gqlTypes.IoTDataLatestDocument,

    variablesBuilder: (ctx: ActivityContext, asset?: gqlTypes.AssetBasic, start?: DateTime) => {
      // Get asset from order (assets are now assigned at order level)
      const assetId = asset?.id ?? ctx.order?.assets?.[0]?.id
      const endEvent = ctx.activity.events?.find((e) => e.kind === gqlTypes.ActivityEventKind.Completed)

      const stopTime = endEvent?.created_at
        ? Math.floor(new Date(endEvent.created_at).getTime() / 1000)
        : Math.floor(Date.now() / 1000)

      const startTime = start ? toTimestamp(start, true) : stopTime - LAST_30_DAYS // Last 30 days

      return {
        installations: "",
        assets: assetId,
        field: "weight",
        start: startTime,
        stop: stopTime,
        location: "Europe/London",
      }
    },

    transformer: (iotData: any) => {
      return iotData?.getIoTDataLatest?.value?.toFixed(2) || null
    },

    cacheDuration: 60000,
    loadingPlaceholder: "Reading weight sensor...",
    fallbackValue: null,
  }
}

/**
 * Create IoT source for tank level sensor readings
 */
export function fillLevelIoTSource(): IoTSourceConfig {
  return {
    query: gqlTypes.IoTDataLatestDocument,

    variablesBuilder: (ctx: ActivityContext, asset?: gqlTypes.AssetBasic, start?: DateTime) => {
      // Get asset from order (assets are now assigned at order level)
      const assetId = asset?.id ?? ctx.order?.assets?.[0]?.id
      const endEvent = ctx.activity.events?.find((e) => e.kind === gqlTypes.ActivityEventKind.Completed)

      const stopTime = endEvent?.created_at
        ? Math.floor(new Date(endEvent.created_at).getTime() / 1000)
        : Math.floor(Date.now() / 1000)

      const startTime = start ? toTimestamp(start, true) : stopTime - LAST_30_DAYS // Last 30 days

      return {
        installations: "",
        assets: assetId,
        field: "weight",
        start: startTime,
        stop: stopTime,
        location: "Europe/London",
      }
    },

    transformer: (iotData: any) => {
      return iotData?.getIoTDataLatest?.value?.toFixed(1) || null
    },

    cacheDuration: 60000,
    loadingPlaceholder: "Reading tank level sensor...",
    fallbackValue: null,
  }
}
